import Phaser from 'phaser';
import { audioManager } from '@/systems/AudioManager';
import { saveSystem } from '@/systems/SaveSystem';
import type { PerTableStats } from '@/types';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

export interface TrainingResultPayload {
  score: number;
  correctCount: number;
  wrongCount: number;
  speedBonusRate: number;
  maxCombo: number;
  totalCorrectResponseTimeMs: number;
  perTable: Record<number, PerTableStats>;
  durationMs: number;
  tables: number[];
  paceLabel: string;
}

/**
 * TrainingResultScene — sumário do Treino Livre.
 *
 * Não tem estrelas (não é fase): foca em métricas — corretas, %
 * turbo, melhor combo e tempo de prática. Persiste no SaveSystem
 * (sem levelKey/stars) para que o XP, perTable e streakRecord
 * acumulem como em qualquer rodada.
 */
export class TrainingResultScene extends Phaser.Scene {
  private payload!: TrainingResultPayload;
  private xpLabel?: Phaser.GameObjects.Text;
  private rankUpLabel?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'TrainingResultScene' });
  }

  init(data: TrainingResultPayload): void {
    this.payload = data;
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const { width, height } = this.scale;

    this.add
      .text(width / 2, height * 0.16, 'Treino encerrado', {
        fontFamily: Typography.fontFamily,
        fontSize: '48px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.24, this.headerLine(), {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.4, `${this.payload.score} pontos`, {
        fontFamily: Typography.fontFamily,
        fontSize: '64px',
        color: Palette.accentCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.renderStatBlock();

    this.xpLabel = this.add
      .text(width / 2, height * 0.66, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.successCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.rankUpLabel = this.add
      .text(width / 2, height * 0.72, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: Palette.warningCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    new Button(this, width / 2 - 160, height * 0.86, 'Treinar de novo', {
      width: 280,
      height: 64,
      fontSize: 22,
      onClick: () => fadeToScene(this, 'TrainingPickScene'),
    });

    new Button(this, width / 2 + 160, height * 0.86, 'Voltar ao menu', {
      width: 280,
      height: 64,
      fontSize: 22,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.accent,
      onClick: () => fadeToScene(this, 'MenuScene'),
    });

    void this.persistSession();
  }

  private renderStatBlock(): void {
    const { width, height } = this.scale;
    const lines = [
      `${this.payload.correctCount} corretas · ${this.payload.wrongCount} erros`,
      `Melhor combo: ${this.payload.maxCombo}`,
      `${Math.round(this.payload.speedBonusRate * 100)}% no modo turbo`,
      `Tempo de prática: ${this.formatDuration(this.payload.durationMs)}`,
    ];
    lines.forEach((line, i) => {
      this.add
        .text(width / 2, height * 0.5 + i * 28, line, {
          fontFamily: Typography.fontFamily,
          fontSize: '20px',
          color: Palette.textMutedCss,
        })
        .setOrigin(0.5);
    });
  }

  private async persistSession(): Promise<void> {
    try {
      const outcome = await saveSystem.recordSession({
        scoreGained: this.payload.score,
        correctCount: this.payload.correctCount,
        wrongCount: this.payload.wrongCount,
        totalCorrectResponseTimeMs: this.payload.totalCorrectResponseTimeMs,
        maxCombo: this.payload.maxCombo,
        perTable: this.payload.perTable,
        // Sem levelKey/stars: Treino Livre não conta para estrelas de fase.
      });

      if (!this.scene.isActive()) return;

      if (outcome.xpGained > 0 && this.xpLabel) {
        this.xpLabel.setText(`+${outcome.xpGained} XP`);
        this.tweens.add({
          targets: this.xpLabel,
          alpha: 1,
          y: this.xpLabel.y - 10,
          duration: 420,
          ease: 'Cubic.easeOut',
        });
      }

      if (outcome.rankUp && this.rankUpLabel) {
        this.rankUpLabel.setText(`Subiu de rank! ${outcome.rankAfter.name}`);
        this.tweens.add({
          targets: this.rankUpLabel,
          alpha: 1,
          scale: { from: 0.7, to: 1 },
          duration: 480,
          delay: 220,
          ease: 'Back.easeOut',
        });
        this.time.delayedCall(220, () => audioManager.playSfx('rankUp'));
      }
    } catch (err) {
      console.error('[TrainingResultScene] Falha ao persistir sessão', err);
    }
  }

  private headerLine(): string {
    const tables = this.payload.tables.length === 12 ? 'Todas' : this.payload.tables.join(', ');
    return `Tabuadas: ${tables} · Ritmo: ${this.payload.paceLabel}`;
  }

  private formatDuration(ms: number): string {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    if (min === 0) return `${sec}s`;
    return `${min}m ${sec.toString().padStart(2, '0')}s`;
  }
}
